import React from "react";
import { styled } from "@mui/material/styles";
import GrubHub from "../media/grubhub.png";
import DoorDash from "../media/doordash.png";
import UberEats from "../media/ubereats.png";
import OpenTable from "../media/opentable.png";
import Resy from "../media/resy.png";
import { usePostHog } from "posthog-js/react";

// Styled Components (MUI syntax)
const Container = styled("div")({
  marginTop: "1.5rem",
  marginBottom: "1.5rem",
});

const Title = styled("h3")({
  fontSize: "1.125rem",
  fontWeight: 600,
  color: "#111827",
  marginBottom: "-0.75rem",
});

const Description = styled("p")({
  fontSize: "0.875rem",
  color: "#6b7280",
  marginBottom: "1rem",
});

const IconsContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
});

const IconLink = styled("a")({
  backgroundColor: "white",
  width: "3.5rem",
  height: "3.5rem",
  borderRadius: "0.5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 200ms ease-out",
  transform: "scale(1)",
  textDecoration: "none",
  overflow: "hidden",

  "&:hover": {
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    transform: "scale(1.05)",
  },

  "&:active": {
    transform: "scale(0.95)",
  },
});

const Logo = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

/**
 * DeliveryLinks Component
 *
 * Displays links to search for a restaurant on various delivery
 * and reservation platforms.
 *
 * Expected props shape:
 * {
 *   restaurant: {
 *     name: string,
 *     city: "Metairie, LA 70002",
 *     ...
 *   },
 *   address: string,
 *   affiliateIds?: {
 *     ubereats?: string,
 *     doordash?: string,
 *     grubhub?: string,
 *     opentable?: string,
 *     resy?: string,
 *   }
 * }
 */
const DeliveryLinks = (props) => {
  const posthog = usePostHog();
  const innerRestaurant = props?.restaurant || {};
  const address = props?.address || "";
  const affiliateIds = props?.affiliateIds || {};

  const restaurantName = innerRestaurant.name || "";

  // Track delivery link clicks
  const handleLinkClick = (platform, url) => {
    posthog?.capture("delivery_link_clicked", {
      platform: platform,
      restaurant_name: restaurantName,
      url: url,
      has_affiliate_id: !!affiliateIds[platform],
    });
  };

  // Generate search URLs with affiliate tracking
  const generateSearchUrl = (platform) => {
    const encodedName = encodeURIComponent(restaurantName);
    const encodedAddress = encodeURIComponent(address); // not used yet, but handy to have

    const urls = {
      ubereats: {
        base: "https://www.ubereats.com", // Generic homepage (mobile opens app)
        params: "",
        affiliate: affiliateIds.ubereats || "",
      },
      doordash: {
        base: "https://www.doordash.com", // Generic homepage
        params: "",
        affiliate: affiliateIds.doordash || "",
      },
      grubhub: {
        base: "https://www.grubhub.com", // Generic homepage
        params: "",
        affiliate: affiliateIds.grubhub || "",
      },
      opentable: {
        base: "https://www.opentable.com", // Generic homepage
        params: "",
        affiliate: affiliateIds.opentable || "",
      },
      resy: {
        base: "https://resy.com", // Generic homepage
        params: "",
        affiliate: affiliateIds.resy || "",
      },
    };

    const platformData = urls[platform];
    if (!platformData) return "#";

    let url = platformData.base + (platformData.params || "");

    // Add affiliate tracking if provided
    if (platformData.affiliate) {
      // If params already exist (start with ?), append with &, else start with ?
      const separator = url.includes("?") ? "&" : "?";
      url += `${separator}${platformData.affiliate}`;
    }

    return url;
  };

  const platforms = [
    {
      name: "DoorDash",
      key: "doordash",
      logo: DoorDash,
    },
    {
      name: "Grubhub",
      key: "grubhub",
      logo: GrubHub,
    },
    {
      name: "Uber Eats",
      key: "ubereats",
      logo: UberEats,
    },
    {
      name: "OpenTable",
      key: "opentable",
      logo: OpenTable,
    },
    {
      name: "Resy",
      key: "resy",
      logo: Resy,
    },
  ];

  return (
    <Container>
      <Title>Order & Reserve</Title>
      <Description>
        Search for this restaurant on your favorite app:
      </Description>

      <IconsContainer>
        {platforms.map((platform) => {
          const url = generateSearchUrl(platform.key);
          return (
            <IconLink
              key={platform.key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={platform.name}
              onClick={() => handleLinkClick(platform.key, url)}
            >
              <Logo src={platform.logo} alt={platform.name} />
            </IconLink>
          );
        })}
      </IconsContainer>
    </Container>
  );
};

export default DeliveryLinks;
